import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RequireAdmin } from './components/RequireAdmin';
import { Shell } from './components/Shell';
import { LoginPage } from './pages/LoginPage';
import { BookingsPage } from './pages/bookings/BookingsPage';
import { CoachesPage } from './pages/coaches/CoachesPage';
import { CoachProfilesPage } from './pages/coach-profiles/CoachProfilesPage';
import { CoachCallbacksPage } from './pages/coach-callbacks/CoachCallbacksPage';
import { NewCoachSeriesPage } from './pages/coaches/NewCoachSeriesPage';
import { NewOneTimeCoachSessionPage } from './pages/coaches/NewOneTimeCoachSessionPage';
import { CoachSeriesDetailPage } from './pages/coaches/CoachSeriesDetailPage';
import { DoorsPage } from './pages/doors/DoorsPage';
import { StreamsPage } from './pages/streams/StreamsPage';
import { UsersPage } from './pages/users/UsersPage';
import { UserDetailPage } from './pages/users/UserDetailPage';
import { TablesPage } from './pages/tables/TablesPage';
import { NewBookingPage } from './pages/bookings/NewBookingPage';
import { T, PS } from './lib/theme';
import { StatsPage } from './pages/stats/StatsPage';
import { TournamentsPage } from './pages/tournaments/TournamentsPage';
import { NewTournamentPage } from './pages/tournaments/NewTournamentPage';
import { TournamentDetailPage } from './pages/tournaments/TournamentDetailPage';
import { EditTournamentPage } from './pages/tournaments/EditTournamentPage';
import { TournamentSeriesPage } from './pages/tournaments/TournamentSeriesPage';
import { NewTournamentSeriesPage } from './pages/tournaments/NewTournamentSeriesPage';
import { TournamentSeriesDetailPage } from './pages/tournaments/TournamentSeriesDetailPage';
import { DisplayDevicesPage } from './pages/devices/DisplayDevicesPage';
import { ClubHoursPage } from './pages/club/ClubHoursPage';
import { ClimatePage } from './pages/climate/ClimatePage';
import { VendingPage } from './pages/vending/VendingPage';
import { SalesPage } from './pages/sales/SalesPage';
import { SettingsPage } from './pages/settings/SettingsPage';

function LegacySettingsRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search, hash: location.hash }} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAdmin />}>
        <Route element={<Shell />}>
          <Route path="/" element={<Navigate to="/bookings" replace />} />
          <Route path="/bookings/new" element={<NewBookingPage />} />
          <Route path="/bookings" element={<BookingsPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/users/:id" element={<UserDetailPage />} />
          <Route path="/tables" element={<LegacySettingsRedirect to="/settings/tables" />} />
          <Route path="/coaches" element={<CoachesPage />} />
          <Route path="/coach-profiles" element={<CoachProfilesPage />} />
          <Route path="/coach-callbacks" element={<CoachCallbacksPage />} />
          <Route path="/coaches/series/new" element={<NewCoachSeriesPage />} />
          {/* Before /coaches/series/:id would ever see it — a distinct path, not
              a series id, so ordering is not load-bearing, but keep it adjacent
              to the create route it belongs with. */}
          <Route path="/coaches/one-time/new" element={<NewOneTimeCoachSessionPage />} />
          <Route path="/coaches/series/:id" element={<CoachSeriesDetailPage />} />
          <Route path="/vending" element={<LegacySettingsRedirect to="/settings/vending" />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/sales" element={<SalesPage />} />
          {/* The former manual status-URL editor is retired. Keep bookmarks in
              the tournament workflow without offering a competing URL source. */}
          <Route path="/tournament-status" element={<Navigate to="/tournaments" replace />} />
          <Route path="/display-devices" element={<LegacySettingsRedirect to="/settings/display-devices" />} />
          <Route path="/club-hours" element={<LegacySettingsRedirect to="/settings/club-hours" />} />
          <Route path="/doors" element={<DoorsPage />} />
          <Route path="/climate" element={<LegacySettingsRedirect to="/settings/climate" />} />
          <Route path="/settings" element={<SettingsPage />}>
            <Route index element={<VendingPage />} />
            <Route path="vending" element={<VendingPage />} />
            <Route path="tables" element={<TablesPage />} />
            <Route path="club-hours" element={<ClubHoursPage />} />
            <Route path="climate" element={<ClimatePage />} />
            <Route path="display-devices" element={<DisplayDevicesPage />} />
          </Route>
          <Route path="/streams" element={<StreamsPage />} />
          <Route path="/tournaments/new" element={<NewTournamentPage />} />
          <Route path="/tournaments/series/new" element={<NewTournamentSeriesPage />} />
          {/* Route order here doesn't matter (React Router ranks static
              segments before dynamic ones), but /new stays adjacent to the
              list route it belongs with — same convention as
              /coaches/series/new above. */}
          <Route path="/tournaments/series" element={<TournamentSeriesPage />} />
          <Route path="/tournaments/series/:id" element={<TournamentSeriesDetailPage />} />
          <Route path="/tournaments" element={<TournamentsPage />} />
          <Route path="/tournaments/:id/edit" element={<EditTournamentPage />} />
          <Route path="/tournaments/:id" element={<TournamentDetailPage />} />
        </Route>
      </Route>
      <Route
        path="*"
        element={
          <div style={{ padding: 32, ...T.ui }}>
            Not found.{' '}
            <a href="/" style={{ color: PS.linkBlue }}>
              Back to dashboard
            </a>
          </div>
        }
      />
    </Routes>
  );
}
